/**
 * Vesper useExplore Hook (Shared)
 *
 * Chat-driven discovery of events and live moments.
 * Separate from useCleo (Plan mode) — maintains its own message list
 * and result state.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  getExploreInitial,
  saveEvent as saveEventApi,
  unsaveEvent as unsaveEventApi,
} from '../services/explore.service';
import { getApiBase, API } from '../constants/api';
import { getStorage, resolveStorageValue } from '../storage/storage';
import type { ArkChatMessage, ExploreResult } from '../types/ark.types';

function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function useExplore(resetKey?: string) {
  const [messages, setMessages] = useState<ArkChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<ExploreResult[]>([]);
  const [savedEventIds, setSavedEventIds] = useState<Set<string>>(new Set());
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const streamAbortRef = useRef<AbortController | null>(null);

  const lastResetKeyRef = useRef(resetKey);

  // Reset when resetKey changes (e.g. user logs out/in)
  useEffect(() => {
    if (lastResetKeyRef.current !== resetKey) {
      lastResetKeyRef.current = resetKey;
      setMessages([]);
      setResults([]);
      setSavedEventIds(new Set());
      setError(null);
      setInitialLoaded(false);
    }
  }, [resetKey]);

  const sendMessage = useCallback(async (
    text: string,
    location?: { lat: number; lng: number } | null,
    /** Phase B2 — Local / Remote-OK / Remote-only segmented control. */
    filters?: { remoteMode?: 'local' | 'mixed' | 'remote' },
  ): Promise<void> => {
    if (!text.trim() || isLoading || isStreaming) return;

    const userMessage: ArkChatMessage = {
      id: generateId(),
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };

    // Create a placeholder assistant message that we'll accumulate into
    const assistantId = generateId();
    const assistantMessage: ArkChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    setIsStreaming(true);
    setIsLoading(true);
    setError(null);

    const abortController = new AbortController();
    streamAbortRef.current = abortController;

    try {
      const apiBase = getApiBase();
      const storage = getStorage();
      const token = await resolveStorageValue(storage.getItem('ark_token'));

      const resp = await fetch(`${apiBase}${API.ARK_EXPLORE_STREAM}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: text,
          location: location ?? undefined,
          ...(filters ? { filters } : {}),
        }),
        signal: abortController.signal,
      });

      if (!resp.ok) {
        throw new Error(`Stream request failed: ${resp.status}`);
      }

      const reader = resp.body?.getReader();
      if (!reader) throw new Error('No readable stream');

      const decoder = new TextDecoder();
      let buffer = '';
      let fullReply = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        // Keep the last (potentially incomplete) line in the buffer
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          try {
            const data = JSON.parse(jsonStr);

            if (data.error) {
              throw new Error(data.error);
            }

            if (data.chunk) {
              fullReply += data.chunk;
              // Update the assistant message in-place
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId ? { ...m, content: fullReply } : m,
                ),
              );
            }

            if (data.done) {
              // Final event — update with parsed reply and results
              const finalReply = data.fullReply || fullReply;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId ? { ...m, content: finalReply } : m,
                ),
              );
              setResults(data.results ?? []);
            }
          } catch (parseErr) {
            // If it's a re-thrown error from data.error, propagate it
            if (parseErr instanceof Error && parseErr.message !== 'Unexpected end of JSON input') {
              throw parseErr;
            }
            // Otherwise skip malformed SSE lines
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return; // User cancelled

      // Remove the placeholder assistant message and show error
      setMessages((prev) => prev.filter((m) => m.id !== assistantId));
      setError('Ark is unavailable right now. Try again in a moment.');
    } finally {
      setIsStreaming(false);
      setIsLoading(false);
      streamAbortRef.current = null;
    }
  }, [isLoading, isStreaming]);

  const fetchInitial = useCallback(async (
    location?: { lat: number; lng: number } | null,
  ): Promise<void> => {
    if (initialLoaded) return;
    setIsLoading(true);
    try {
      const response = await getExploreInitial(location);
      if (response.results?.length) {
        setResults(response.results);
      }
      setInitialLoaded(true);
    } catch {
      // Non-fatal — user can still type queries
    } finally {
      setIsLoading(false);
    }
  }, [initialLoaded]);

  const saveEvent = useCallback(async (id: string): Promise<void> => {
    setSavedEventIds((prev) => new Set(prev).add(id));
    try {
      await saveEventApi(id);
    } catch {
      // Revert on failure
      setSavedEventIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, []);

  const unsaveEvent = useCallback(async (id: string): Promise<void> => {
    setSavedEventIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    try {
      await unsaveEventApi(id);
    } catch {
      // Revert on failure
      setSavedEventIds((prev) => new Set(prev).add(id));
    }
  }, []);

  const clearExplore = useCallback((): void => {
    setMessages([]);
    setResults([]);
    setError(null);
    setInitialLoaded(false);
  }, []);

  return {
    messages,
    isLoading,
    isStreaming,
    error,
    results,
    savedEventIds,
    sendMessage,
    fetchInitial,
    saveEvent,
    unsaveEvent,
    clearExplore,
  };
}
